"use client";

import { useTranslation } from "@/lib/i18n/context";

/**
 * Deep-dive for /product/integrations: shows, with illustrative examples, how a
 * task and a calendar event are added and edited — in plain language or a quick
 * command. Tasks use Journail's built-in manager (Todoist optional); calendar
 * is two-way. Real component output, not a static mockup.
 */

function Mono({ children }: { children: React.ReactNode }) {
  return <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-[var(--ink-soft)]/70">{children}</span>;
}

export function IntegrationsDeepDive() {
  const { lang } = useTranslation();
  const en = lang === "en";

  return (
    <section className="relative max-w-[1120px] mx-auto px-6 md:px-12 pb-20 md:pb-28 z-[2]">
      <div className="max-w-[720px] mb-10 md:mb-14">
        <div className="kicker-mono inline-flex items-center gap-2.5 mb-5">
          <span className="w-1.5 h-1.5 rounded-full bg-[var(--terracotta)]" />
          {en ? "// IN PRACTICE" : "// V PRAKSI"}
        </div>
        <h2 className="font-display text-[clamp(28px,3.4vw,44px)] leading-[1.08] tracking-[-0.02em] text-balance" style={{ fontWeight: 380 }}>
          {en ? (
            <>Add and edit in <em className="text-[var(--terracotta)] italic">plain language</em>.</>
          ) : (
            <>Dodajaj in urejaj v <em className="text-[var(--terracotta)] italic">naravnem jeziku</em>.</>
          )}
        </h2>
        <p className="mt-4 text-[16px] md:text-[18px] leading-[1.6] text-[var(--ink-soft)] text-pretty">
          {en
            ? "Journail is a life management app: tasks live in its built-in manager (or Todoist, if you connect it), and calendar events sync both ways with Google Calendar. Here's how a task and an event come to life."
            : "Journail je aplikacija za upravljanje življenja: naloge živijo v vgrajenem upravitelju (ali v Todoistu, če ga povežeš), koledarski dogodki pa se dvosmerno sinhronizirajo z Google Koledarjem. Takole nastaneta naloga in dogodek."}
        </p>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        {/* ── TASK add + edit ───────────────────────────────────────────── */}
        <div className="rounded-2xl border border-[var(--rule)] bg-[var(--paper-2)]/30 p-5 md:p-6">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--terracotta)]">{en ? "// TASK" : "// NALOGA"}</p>

          {/* add */}
          <p className="mt-4 mb-2"><Mono>{en ? "You type" : "Vtipkaš"}</Mono></p>
          <div className="rounded-xl border border-[var(--rule)] bg-[var(--paper)] px-4 py-3 font-display italic text-[15px] text-[var(--ink)]">
            {en ? "“add task: prepare the board deck on Friday, for the Board project, goal Series A”" : "»dodaj nalogo: pripravi gradivo za upravo v petek, projekt Uprava, cilj Series A«"}
          </div>
          <p className="mt-3 mb-2"><Mono>{en ? "Journail creates" : "Journail ustvari"}</Mono></p>
          <div className="rounded-xl border border-[var(--rule)] bg-[var(--paper)] px-4 py-3">
            <div className="flex items-center gap-2">
              <span className="grid h-4 w-4 place-items-center rounded-[5px] border border-[var(--rule)] text-[var(--moss)]">✓</span>
              <span className="text-[14px] text-[var(--ink)]">{en ? "Prepare the board deck" : "Pripravi gradivo za upravo"}</span>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-1.5 pl-6">
              <span className="rounded-full border border-[var(--rule)] px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--ink-soft)]">{en ? "FRI 14:00" : "PET 14:00"}</span>
              <span className="rounded-full border border-[var(--rule)] px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--ink-soft)]">{en ? "Project · Board" : "Projekt · Uprava"}</span>
              <span className="inline-flex items-center gap-1 rounded-full border border-[var(--terracotta)]/30 bg-[var(--terracotta)]/[0.06] px-2 py-0.5">
                <span className="h-1.5 w-1.5 rounded-full bg-[var(--terracotta)]" />
                <span className="font-display italic text-[11.5px] text-[var(--ink)]">Series A</span>
              </span>
            </div>
          </div>

          {/* edit */}
          <p className="mt-4 mb-2"><Mono>{en ? "Then edit" : "Nato urediš"}</Mono></p>
          <div className="rounded-xl border border-[var(--rule)] bg-[var(--paper)] px-4 py-3 text-[13.5px] text-[var(--ink-soft)]">
            {en
              ? <>“move it to Monday and mark high priority” → <span className="text-[var(--ink)]">MON 09:00 · ⚑ High</span></>
              : <>»prestavi na ponedeljek in označi visoko prioriteto« → <span className="text-[var(--ink)]">PON 09:00 · ⚑ Visoka</span></>}
          </div>
          <p className="mt-3 text-[12.5px] leading-[1.55] text-[var(--ink-soft)]/90 italic">
            {en ? "Built-in by default — or written straight to Todoist if you've connected it." : "Privzeto vgrajeno — ali zapisano naravnost v Todoist, če si ga povezal."}
          </p>
        </div>

        {/* ── CALENDAR add + edit ───────────────────────────────────────── */}
        <div className="rounded-2xl border border-[var(--rule)] bg-[var(--paper-2)]/30 p-5 md:p-6">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--terracotta)]">{en ? "// CALENDAR EVENT" : "// KOLEDARSKI DOGODEK"}</p>

          {/* add */}
          <p className="mt-4 mb-2"><Mono>{en ? "You type" : "Vtipkaš"}</Mono></p>
          <div className="rounded-xl border border-[var(--rule)] bg-[var(--paper)] px-4 py-3 font-display italic text-[15px] text-[var(--ink)]">
            {en ? "“schedule an investor call tomorrow at 3pm, add a Meet link, on my Work calendar”" : "»dodaj klic z investitorjem jutri ob 15:00, dodaj Meet povezavo, na koledar Delo«"}
          </div>
          <p className="mt-3 mb-2"><Mono>{en ? "Created in Google Calendar" : "Ustvarjeno v Google Koledarju"}</Mono></p>
          <div className="rounded-xl border border-[var(--rule)] bg-[var(--paper)] px-4 py-3">
            <div className="flex items-center gap-2">
              <span className="h-2.5 w-2.5 rounded-[3px] bg-[var(--moss)]" />
              <span className="text-[14px] text-[var(--ink)]">{en ? "Investor call" : "Klic z investitorjem"}</span>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-1.5 pl-[18px]">
              <span className="rounded-full border border-[var(--rule)] px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--ink-soft)]">{en ? "TOMORROW · 15:00–15:30" : "JUTRI · 15:00–15:30"}</span>
              <span className="rounded-full border border-[var(--rule)] px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--ink-soft)]">{en ? "Calendar · Work" : "Koledar · Delo"}</span>
              <span className="rounded-full border border-[var(--moss)]/30 bg-[var(--moss)]/[0.08] px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--moss)]">Google Meet</span>
            </div>
          </div>

          {/* edit */}
          <p className="mt-4 mb-2"><Mono>{en ? "Then edit" : "Nato urediš"}</Mono></p>
          <div className="rounded-xl border border-[var(--rule)] bg-[var(--paper)] px-4 py-3 text-[13.5px] text-[var(--ink-soft)]">
            {en
              ? <>“move it to 4pm and invite priya@…” → <span className="text-[var(--ink)]">16:00 · +1 guest</span></>
              : <>»prestavi na 16:00 in povabi priya@…« → <span className="text-[var(--ink)]">16:00 · +1 gost</span></>}
          </div>
          <p className="mt-3 text-[12.5px] leading-[1.55] text-[var(--ink-soft)]/90 italic">
            {en ? "Two-way: changes you make in Google Calendar show up in Journail too." : "Dvosmerno: spremembe, ki jih narediš v Google Koledarju, se pokažejo tudi v Journailu."}
          </p>
        </div>
      </div>
    </section>
  );
}
